import { FilesetResolver, ImageSegmenter } from '@mediapipe/tasks-vision';
import { MindSDK } from 'mind-sdk-web';
import { MediaStream } from 'mind-sdk-web/lib/MediaStream';
import { WebGLVideoSupplier } from './webgl-video-supplier';

// Frame rate constant for blur processing and output
export const BLUR_FRAME_RATE = 30; // fps
export const BLUR_AMOUNT = 15;

export class BackgroundBlurrer {
  private videoElement: HTMLVideoElement | null = null;
  private canvasElement: HTMLCanvasElement;
  private imageSegmenter: ImageSegmenter | null = null;
  private videoSupplier: WebGLVideoSupplier | null = null;
  private processingIntervalId: number | null = null;
  private isProcessing: boolean = false;
  private outputStream: MediaStream | null = null;
  private lastProcessTime: number = 0;
  private frameRateLimit: number = BLUR_FRAME_RATE;

  constructor() {
    this.canvasElement = document.createElement('canvas');
  }

  private async createImageSegmenter(
    mode: 'CPU' | 'GPU' = 'GPU',
    lowQualitySegmentation: boolean = false
  ): Promise<ImageSegmenter> {
    const filesetResolver = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.2/wasm'
    );

    return ImageSegmenter.createFromOptions(filesetResolver, {
      baseOptions: {
        modelAssetPath:
          'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
        delegate: mode,
      },
      canvas: this.canvasElement,
      runningMode: 'VIDEO',
      outputCategoryMask: lowQualitySegmentation,
      outputConfidenceMasks: !lowQualitySegmentation,
    });
  }

  /**
   * Warms up the image segmenter by running a dummy segmentation.
   * This ensures internal pipelines are initialized. Without this step,
   * the first real call to `segmentForVideo` may be delayed for several seconds,
   * resulting in a black square in the video output at startup.
   */
  private async warmUpSegmenter(): Promise<void> {
    if (!this.imageSegmenter) return;

    const dummyCanvas = document.createElement('canvas');
    dummyCanvas.width = 1;
    dummyCanvas.height = 1;
    const dummyCtx = dummyCanvas.getContext('2d');
    dummyCtx?.fillRect(0, 0, 1, 1); // Just a black pixel

    const dummyFrame = await createImageBitmap(dummyCanvas);
    await this.imageSegmenter.segmentForVideo(dummyFrame, performance.now());
  }

  public static isSupported(): boolean {
    // Check for WebGL2 support
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2');
    const hasWebGL2 = !!gl;

    // Release the WebGL context to avoid leaks
    if (gl && 'getExtension' in gl) {
      const loseCtx = gl.getExtension('WEBGL_lose_context');
      if (loseCtx) {
        loseCtx.loseContext();
      }
    }

    if (!hasWebGL2) {
      console.warn('WebGL2 is not supported on this device');
      return false;
    }

    // Check for canvas.captureStream
    const canCaptureStream = 'captureStream' in canvas;
    if (!canCaptureStream) {
      console.warn('Canvas captureStream is not supported on this device');
      return false;
    }

    return true;
  }

  public async apply(mediaStream: MediaStream, lowQualitySegmentation: boolean = false): Promise<MediaStream | null> {
    if (!BackgroundBlurrer.isSupported()) {
      console.warn('BackgroundBlurrer: Device not supported');
      return null;
    }

    try {
      // Cleanup any existing resources first
      this.dispose();

      // Create and setup video element
      this.videoElement = document.createElement('video');
      if (!this.videoElement) {
        console.error('BackgroundBlurrer: Failed to create video element');
        return null;
      }

      this.videoElement.autoplay = true;
      this.videoElement.playsInline = true;
      this.videoElement.muted = true;
      this.videoElement.mediaStream = mediaStream;

      // Initialize image segmenter with CPU mode as fallback if GPU fails
      let segmenterInitAttempts = 0;
      const maxAttempts = 2;

      while (segmenterInitAttempts < maxAttempts && !this.imageSegmenter) {
        try {
          segmenterInitAttempts++;
          const mode = segmenterInitAttempts === 1 ? 'GPU' : 'CPU';

          this.imageSegmenter = await this.createImageSegmenter(mode, lowQualitySegmentation);
        } catch (error) {
          console.warn(`[WebGLBlur] Failed to initialize segmenter (attempt ${segmenterInitAttempts}):`, error);

          if (segmenterInitAttempts >= maxAttempts) {
            throw new Error('Failed to initialize segmenter after multiple attempts');
          }
        }
      }

      // Warm up the segmenter to avoid delays to get the first frame
      await this.warmUpSegmenter();

      // Create video supplier using WebGL
      const isMobileBrowser = navigator.maxTouchPoints > 1;
      const adaptivity = isMobileBrowser ? 2 : 3; // for 720p - 3 (720p, 360p, 180p), for 360p - 2 (360p, 180p)
      this.videoSupplier = new WebGLVideoSupplier(this.canvasElement, this.frameRateLimit, BLUR_AMOUNT, adaptivity);

      // Create output stream
      this.outputStream = MindSDK.createMediaStream(
        mediaStream.hasAudioSupplier() ? mediaStream : null,
        this.videoSupplier
      );

      this.outputStream?.setMaxVideoFrameArea(isMobileBrowser ? 640 * 360 : 1280 * 720);
      this.outputStream?.setMaxVideoFrameRate(30);
      this.outputStream?.setMaxVideoBitrate(12_000_000); // 12 Mbps

      // Start playing video
      await this.videoElement.play();

      // Start processing frames using setInterval instead of requestAnimationFrame
      this.isProcessing = true;
      this.lastProcessTime = performance.now();

      // Use interval slightly faster than frameRateLimit to ensure we don't miss frames
      const intervalMs = Math.floor(1000 / this.frameRateLimit);
      this.processingIntervalId = window.setInterval(() => this.processFrame(), intervalMs);

      return this.outputStream;
    } catch (error) {
      console.error('BackgroundBlurrer: Initialization failed:', error);

      // Specifically handle WebRTC parameter errors
      if (
        error instanceof RangeError &&
        error.message.includes('setParameters') &&
        error.message.includes('scale_resolution_down_by')
      ) {
        console.warn('WebRTC scaling parameter error detected. Disabling background blur.');
      }

      this.dispose();
      return null;
    }
  }

  public dispose(): void {
    this.isProcessing = false;

    if (this.processingIntervalId) {
      clearInterval(this.processingIntervalId);
      this.processingIntervalId = null;
    }

    if (this.imageSegmenter) {
      this.imageSegmenter.close();
      this.imageSegmenter = null;
    }

    if (this.videoElement) {
      this.videoElement.mediaStream = null;
      this.videoElement = null;
    }

    this.videoSupplier = null;
    this.outputStream = null;
  }

  private processFrame(): void {
    if (!this.isProcessing || !this.imageSegmenter || !this.videoElement || !this.videoSupplier) {
      return;
    }

    const now = performance.now();
    const elapsed = now - this.lastProcessTime;
    const frameInterval = 1000 / this.frameRateLimit;

    // Limit frame rate to reduce CPU usage
    if (elapsed < frameInterval) {
      return;
    }

    const video = this.videoElement;

    if (video.videoWidth === 0 || video.videoHeight === 0) {
      return;
    }

    if (video.readyState === 4 && !video.paused) {
      try {
        const timestamp = now;
        const segmentationResult = this.imageSegmenter.segmentForVideo(video, timestamp);
        this.videoSupplier.drawFrame(video, segmentationResult);
        // Close the segmentation result to prevent memory leaks
        segmentationResult.close();
        this.lastProcessTime = now;
      } catch (error) {
        console.error('[WebGLBlur] Error processing frame:', error);
      }
    }
  }

  public getStream(): MediaStream | null {
    return this.outputStream;
  }

  public setBlurAmount(amount: number): void {
    if (this.videoSupplier) {
      this.videoSupplier.setBlurAmount(amount);
    }
  }
}
